import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { Stage } from './swimlanes/kanban/types'

export interface StageToRemove {
	id: string
	name: string
	candidateCount: number
}

export interface PipelineResetModalProps {
	isOpen: boolean
	onClose: () => void
	stagesToRemove: StageToRemove[]
	targetStages: Stage[]
	onConfirm: (migrations: Record<string, string>) => void
	isResetting?: boolean
	errorMessage?: string | null
}

export function PipelineResetModal({
	isOpen,
	onClose,
	stagesToRemove,
	targetStages,
	onConfirm,
	isResetting = false,
	errorMessage = null,
}: PipelineResetModalProps) {
	const { t } = useTranslation('common')
	// Track migration destinations: stageId -> targetStageId
	const [migrations, setMigrations] = useState<Record<string, string>>({})

	if (!isOpen) { return null }

	// Only stages with candidates need migration selection
	const stagesNeedingMigration = stagesToRemove.filter(s => s.candidateCount > 0)

	// Check if all stages with candidates have a destination selected
	const allMigrationsSelected = stagesNeedingMigration.every(s => migrations[s.id])

	const handleMigrationChange = (stageId: string, targetStageId: string) => {
		setMigrations(prev => ({
			...prev,
			[stageId]: targetStageId,
		}))
	}

	const handleConfirm = () => {
		if (allMigrationsSelected) {
			onConfirm(migrations)
		}
	}

	const handleClose = () => {
		setMigrations({})
		onClose()
	}

	return (
		<div className="fixed inset-0 z-50 flex items-center justify-center">
			{/* Backdrop */}
			<div
				className="absolute inset-0 bg-black/50"
				onClick={handleClose}
			/>

			{/* Modal */}
			<div className="relative bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 p-6">
				<h2 className="text-lg font-semibold text-gray-900 mb-2">
					{t('pipeline.resetTitle')}
				</h2>

				<p className="text-sm text-gray-600 mb-4">
					{t('pipeline.resetDescription')}
				</p>

				{stagesToRemove.length > 0 && (
					<div className="mb-4">
						<p className="text-sm font-medium text-gray-700 mb-2">
							{t('pipeline.stagesToRemove')}
						</p>
						<div className="space-y-3 max-h-64 overflow-y-auto">
							{stagesToRemove.map((stage) => (
								<div
									key={stage.id}
									className="p-3 bg-gray-50 rounded-md border border-gray-200"
								>
									<div className="flex items-center justify-between mb-2">
										<span className="font-medium text-gray-900">{stage.name}</span>
										<span className="text-sm text-gray-500">
											{t('pipeline.candidateCount', { count: stage.candidateCount })}
										</span>
									</div>
									{stage.candidateCount > 0 && (
										<div>
											<label className="text-sm text-gray-600 block mb-1">
												{t('pipeline.moveCandidatesTo')}
											</label>
											<select
												value={migrations[stage.id] ?? ''}
												onChange={(e) => handleMigrationChange(stage.id, e.target.value)}
												className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
												disabled={isResetting}
											>
												<option value="">{t('pipeline.selectStage')}</option>
												{targetStages.map((target) => (
													<option key={target.id} value={target.id}>
														{target.name}
													</option>
												))}
											</select>
										</div>
									)}
								</div>
							))}
						</div>
					</div>
				)}

				{stagesToRemove.length === 0 && (
					<p className="text-sm text-gray-600 mb-4">
						{t('pipeline.alreadyMatchesDefaults')}
					</p>
				)}

				{errorMessage && (
					<p className="text-sm text-red-600 mb-4">
						{errorMessage}
					</p>
				)}

				<div className="flex gap-3 justify-end">
					<button
						type="button"
						onClick={handleClose}
						disabled={isResetting}
						className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50"
					>
						{t('common.cancel')}
					</button>
					<button
						type="button"
						onClick={handleConfirm}
						disabled={!allMigrationsSelected || isResetting}
						className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
					>
						{isResetting && (
							<svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
								<circle
									className="opacity-25"
									cx="12"
									cy="12"
									r="10"
									stroke="currentColor"
									strokeWidth="4"
									fill="none"
								/>
								<path
									className="opacity-75"
									fill="currentColor"
									d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
								/>
							</svg>
						)}
						{t('pipeline.resetPipeline')}
					</button>
				</div>
			</div>
		</div>
	)
}
